import { useEffect, useState, useMemo, useRef } from "react";
import { ChevronDown, MoreVertical, LogOut, User as UserIcon } from "lucide-react";
import { useApp } from "@/App";

const SLOGAN = {
  de: ["Gemeinsam.", "Stark.", "Unaufhaltsam."],
  en: ["Together.", "Strong.", "Unstoppable."],
  it: ["Insieme.", "Forti.", "Inarrestabili."],
  tr: ["Birlikte.", "Güçlü.", "Durdurulamaz."],
  ru: ["Вместе.", "Сильные.", "Неудержимые."],
  es: ["Juntos.", "Fuertes.", "Imparables."],
  fr: ["Ensemble.", "Forts.", "Inarrêtables."],
  pl: ["Razem.", "Silni.", "Niepowstrzymani."], sr: ["Заједно.", "Јаки.", "Неукротиви."], hr: ["Zajedno.", "Snažno.", "Nezaustavljivo."], sq: ["Bashkë.", "Të fortë.", "Të pandalshëm."], sl: ["Skupaj.", "Močni.", "Neustavljivi."], pt: ["Juntos.", "Fortes.", "Imparáveis."], cs: ["Spolu.", "Silní.", "Nezastavitelní."], hu: ["Együtt.", "Erősek.", "Megállíthatatlanok."], hi: ["साथ में।", "मजबूत।", "अजेय।"], ms: ["Bersama.", "Kuat.", "Tak boleh dihentikan."], nl: ["Samen.", "Sterk.", "Onstuitbaar."], zh: ["一起。", "更强。", "势不可挡。"]
};

/**
 * Accessible sidebar with collapsible groups.
 *  - Groups: role=button, aria-expanded, aria-controls
 *  - Sub-panel: role=region, aria-hidden while collapsed, inert when hidden
 *  - Skip-to-main-content link (visually hidden until focus)
 *  - Consistent focus rings via focus-visible:ring
 *  - Keyboard: TAB navigates, Enter/Space toggles groups (native button behaviour)
 */
export const Sidebar = ({ items, active, onSelect, children, branding, user, onLogout, onOpenProfile }) => {
  const slogan = SLOGAN[useApp()?.lang] || SLOGAN.de;
  const [open, setOpen] = useState({});
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const teamLogo = branding?.logo_url || "/logo.png";
  const teamName = branding?.name || "Team Phoenix";

  useEffect(() => {
    if (!userMenuOpen) return;
    const onDocClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [userMenuOpen]);

  const roleLabel = (role) => {
    if (!role) return "";
    if (role === "admin") return "Administrator";
    if (role === "moderator") return "Moderator";
    if (role === "support" || role === "leader") return "Leader";
    if (role === "translator") return "Übersetzer";
    return "Mitglied";
  };
  const initials = (name) => (name || "?").split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  useEffect(() => {
    setOpen((prev) => {
      const next = { ...prev };
      const walk = (list) => {
        for (const it of list || []) {
          if (it.children) {
            if (it.children.some((c) => c.id === active || (c.children && (c.children.some((g) => g.id === active))))) {
              next[it.id] = true;
            }
            walk(it.children);
          }
        }
      };
      walk(items);
      return next;
    });
  }, [active, items]);

  const toggle = (id) => setOpen((p) => ({ ...p, [id]: !p[id] }));

  const focusRing =
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar";

  // Recursive: leaves + groups (with optional nested groups).
  const containsActiveDeep = (list) =>
    (list || []).some((c) => c.id === active || (c.children && containsActiveDeep(c.children)));

  const renderLeaf = (it, depth = 0) => {
    const isActive = active === it.id;
    const indent = depth === 0 ? "" : depth === 1 ? "pl-9 text-[13px]" : "pl-14 text-[12px]";
    return (
      <button
        key={it.id}
        type="button"
        data-testid={`sidebar-${it.id}`}
        onClick={() => onSelect(it.id)}
        aria-current={isActive ? "page" : undefined}
        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${focusRing} ${indent} ${
          isActive
            ? "bg-primary/15 text-primary shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.25)]"
            : "text-sidebar-muted hover:text-sidebar-fg hover:bg-sidebar-hover"
        }`}
      >
        {it.icon && <it.icon className={`h-4 w-4 shrink-0 ${isActive ? "text-primary" : ""}`} aria-hidden="true" />}
        <span className="truncate flex-1 text-left">{it.label}</span>
        {it.badge > 0 && (
          <span data-testid={`sidebar-badge-${it.id}`} className="text-[10px] bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 font-bold min-w-5 text-center tabular-nums">
            {it.badge > 99 ? "99+" : it.badge}
          </span>
        )}
      </button>
    );
  };

  const childBadge = (list) => (list || []).reduce((n, c) => n + (c.badge || 0) + childBadge(c.children), 0);

  const renderItem = (it, depth = 0) => {
    if (!it.children) return renderLeaf(it, depth);
    const isOpen = !!open[it.id];
    const containsActive = containsActiveDeep(it.children);
    const groupActive = active === it.id || containsActive;
    const panelId = `sidebar-panel-${it.id}`;
    const indent = depth === 0 ? "" : depth === 1 ? "pl-9 text-[13px]" : "pl-14 text-[12px]";
    return (
      <div key={it.id} data-testid={`sidebar-group-${it.id}`}>
        <button
          type="button"
          onClick={() => {
            toggle(it.id);
            if (it.selectable) onSelect(it.id);
          }}
          data-testid={`sidebar-group-toggle-${it.id}`}
          aria-expanded={isOpen}
          aria-controls={panelId}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${focusRing} ${indent} ${
            groupActive ? "text-sidebar-fg bg-sidebar-hover" : "text-sidebar-muted hover:text-sidebar-fg hover:bg-sidebar-hover"
          }`}
        >
          {it.icon && <it.icon className={`h-4 w-4 shrink-0 ${groupActive ? "text-primary" : ""}`} aria-hidden="true" />}
          <span className="flex-1 text-left truncate">{it.label}</span>
          {!isOpen && childBadge(it.children) > 0 && (
            <span data-testid={`sidebar-group-badge-${it.id}`} className="text-[10px] bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 font-bold min-w-5 text-center tabular-nums">
              {childBadge(it.children)}
            </span>
          )}
          <ChevronDown
            className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </button>
        <div
          id={panelId}
          role="region"
          aria-label={it.label}
          aria-hidden={!isOpen}
          className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
            isOpen ? "max-h-[1200px] opacity-100 mt-0.5" : "max-h-0 opacity-0"
          }`}
        >
          <div className="space-y-0.5 py-0.5" {...(isOpen ? {} : { inert: "" })}>
            {it.children.map((c) => renderItem(c, depth + 1))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-3 focus:py-1.5 focus:rounded-md focus:bg-primary focus:text-primary-foreground focus:text-sm focus:font-medium"
        data-testid="skip-to-content"
      >
        Zum Inhalt springen
      </a>
      <aside
        data-testid="sidebar"
        className="hidden lg:flex flex-col w-64 shrink-0 sticky top-0 h-screen border-r border-sidebar-border bg-sidebar text-sidebar-fg px-4 py-6 overflow-y-auto"
        aria-label="Hauptnavigation"
      >
        <div className="flex items-center gap-2.5 px-2 mb-8">
          <div className="h-14 w-14 rounded-xl bg-white border border-sidebar-border flex items-center justify-center p-1.5 shadow-sm shrink-0">
            <img src={teamLogo} alt={teamName} className="h-full w-full object-contain" />
          </div>
          <span className="font-heading font-bold text-sidebar-fg leading-tight flex flex-col">
            <span className="text-lg tracking-tight">{teamName}</span>
            <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-sidebar-muted -mt-0.5">Team Portal</span>
          </span>
        </div>

        <nav className="space-y-0.5" aria-label="Portal-Bereiche">
          {items.map((it) => renderItem(it, 0))}
        </nav>
        <div className="mt-auto pt-6 space-y-3">
          {/* Sidebar footer slogan – Team Phoenix motto */}
          <div className="text-center select-none pointer-events-none pb-1" aria-hidden="true">
            <div className="font-handwritten text-2xl leading-[1.15] text-[#B45309] dark:text-amber-300 relative inline-block">
              {slogan.map((w) => <span key={w} className="block">{w}</span>)}
              <svg viewBox="0 0 160 8" className="mx-auto mt-0.5 h-1.5 w-32 text-[#F97316] dark:text-amber-400" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                <path d="M2 5 C 40 0, 100 8, 158 3" />
              </svg>
            </div>
          </div>
          {user && (
            <div className="relative" ref={userMenuRef}>
              <div className="flex items-center gap-3 rounded-xl bg-sidebar-hover border border-sidebar-border px-3 py-2.5">
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="h-10 w-10 rounded-full object-cover border border-sidebar-border shrink-0" />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-primary/20 text-primary flex items-center justify-center font-semibold text-sm shrink-0">
                    {initials(user.display_name || user.email)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-sidebar-fg truncate" data-testid="sidebar-user-name">{user.display_name || user.email?.split("@")[0] || "–"}</div>
                  <div className="text-[11px] text-sidebar-muted truncate">{(user.roles?.length ? user.roles : [user.role]).map(roleLabel).join(" · ")}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen((v) => !v)}
                  className="h-8 w-8 rounded-lg text-sidebar-muted hover:text-sidebar-fg hover:bg-sidebar-hover flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                  aria-haspopup="menu"
                  aria-expanded={userMenuOpen}
                  aria-label="Benutzer-Menü"
                  data-testid="sidebar-user-menu-toggle"
                >
                  <MoreVertical className="h-4 w-4" />
                </button>
              </div>
              {userMenuOpen && (
                <div
                  role="menu"
                  className="absolute bottom-full left-0 right-0 mb-2 rounded-xl border border-sidebar-border bg-sidebar shadow-lg overflow-hidden"
                  data-testid="sidebar-user-menu"
                >
                  {onOpenProfile && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setUserMenuOpen(false); onOpenProfile(); }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-sidebar-fg hover:bg-sidebar-hover transition-colors"
                      data-testid="sidebar-user-menu-profile"
                    >
                      <UserIcon className="h-4 w-4" /> Profil / Einstellungen
                    </button>
                  )}
                  {onLogout && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setUserMenuOpen(false); onLogout(); }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-destructive hover:bg-destructive/10 transition-colors"
                      data-testid="sidebar-user-menu-logout"
                    >
                      <LogOut className="h-4 w-4" /> Abmelden
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          {children}
        </div>
      </aside>
    </>
  );
};
